import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { PlanTimeframe } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { type ProcurementPlanDetail } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import { now as serverNow } from '~/models/clock'
import { fromProtoDate } from '~/models/dateTime'

/**
 * 対象日・エリア変更後の計画を探す時に、表示中の断面で絞り込むか。
 * 全断面を列挙し、断面が増えた時に typecheck で判断を迫る (列挙漏れを
 * 「絞り込まない」へ黙って倒さない)。UNSPECIFIED は request に載せられない
 * (proto の not_in: [0]) ので絞り込まない。
 */
const NARROWS_LOOKUP_BY_TIMEFRAME: Record<PlanTimeframe, boolean> = {
  [PlanTimeframe.UNSPECIFIED]: false,
  [PlanTimeframe.TODAY]: false,
  [PlanTimeframe.TOMORROW]: false,
  [PlanTimeframe.DAY_AFTER_TOMORROW]: false,
  [PlanTimeframe.WEEKLY]: true,
  [PlanTimeframe.MONTHLY]: true,
  [PlanTimeframe.ANNUAL]: true,
}

/**
 * フィルタバー (DetailFilterBar) の初期値。detail レスポンスから導出する
 * 独立していない state (procurement-plan-detail-filter 設計書 §5)。
 */
export type FilterInitialValues = {
  date: Date
  area: AreaCode
  /** フィルタ変更で送らない不変値 (= 同一計画の BG コード)。 */
  bgCode: string
  /**
   * フィルタ変更後の計画を探す時に絞り込む断面。当日・翌日・翌々日は 1 つの
   * 計画の断面違いで、対象日ごとに計画が持つ断面は異なるため絞り込まない
   * (= undefined。backend が単日断面を横断して最新版を返す、#7072)。
   * 週間・月間・年間は断面ごとに別の計画なので表示中の断面で絞り込む。
   */
  lookupTimeframe: PlanTimeframe | undefined
  /** 初期表示は常に BG 全体 (design §5)。 */
  retailerSelection: 'bg'
}

/**
 * ProcurementPlanDetail レスポンスからフィルタバーの初期値を組み立てる純関数。
 * detail が無い (未取得) 場合は undefined を返す。
 */
export function buildFilterInitialValues(
  detail: ProcurementPlanDetail | undefined,
): FilterInitialValues | undefined {
  if (detail === undefined) return undefined
  return {
    date:
      detail.targetPeriodStartDate !== undefined
        ? fromProtoDate(detail.targetPeriodStartDate)
        : serverNow(),
    area: detail.areaCode,
    bgCode: detail.bgCode,
    lookupTimeframe: NARROWS_LOOKUP_BY_TIMEFRAME[detail.timeframe]
      ? detail.timeframe
      : undefined,
    retailerSelection: 'bg',
  }
}
